import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { Calendar, User, Eye, Smartphone, Monitor, Lock, Clock, CheckCircle2 } from "lucide-react";
import { getAppIcon } from "@/features/tests/appIcons";

export interface TestQuestion {
  id: string;
  type: "multiple_choice" | "rating" | "text" | "task_check";
  text: string;
  options?: string[];
  checklist?: string[];
}

export interface TestProject {
  id: string;
  title: string;
  category: "app-tests" | "web-tests";
  subcategory: string;
  platform: "mobile" | "web";
  appName: string;
  client?: string;
  instructions: string;
  questions: TestQuestion[];
  reward: number;
  deadline: string;
  owner: string;
  status?: "open" | "closed";
  demand?: "low" | "medium" | "high";
  estimatedTime?: string;
  requirements?: string[];
  language?: string;
}

interface TestCardProps {
  project: TestProject;
  onPreview: (project: TestProject) => void;
  isLocked?: boolean;
  onLockedClick?: (project: TestProject) => void;
  isCompleted?: boolean;
}

export function TestCard({
  project,
  onPreview,
  isLocked = false,
  onLockedClick,
  isCompleted = false,
}: TestCardProps) {
  const completed = isCompleted;
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const formatMoney = (value: number) => `$${(value / 100).toFixed(2)}`;

  const BrandIcon = getAppIcon(project.appName);

  const renderBrandIcon = () => {
    if (!BrandIcon) return null;
    if (typeof BrandIcon === "string") {
      return (
        <img
          src={BrandIcon}
          alt={project.appName}
          className="h-6 w-6 object-contain shrink-0 rounded-sm"
          loading="lazy"
        />
      );
    }
    return <BrandIcon className="h-6 w-6 shrink-0" />;
  };

  return (
    <Card
      className={`flex flex-col h-full transition-shadow hover:shadow-lg border-blue-200 ${isLocked ? "opacity-75" : ""}`}
    >
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <Badge className={`bg-blue-100 text-blue-700 border-0`}>{project.subcategory}</Badge>
          <div className="flex-1" />
          {project.estimatedTime && (
            <span className="flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-bold text-blue-700">
              <Clock className="h-3 w-3" />
              {project.estimatedTime}
            </span>
          )}
          {renderBrandIcon()}
          {isLocked && (
            <span className="flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-700">
              <Lock className="h-3 w-3" />
              Locked
            </span>
          )}
          {completed && !isLocked && (
            <span className="flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-700">
              <CheckCircle2 className="h-3 w-3" />
              Completed
            </span>
          )}
        </div>
        <h3 className="mt-2 text-base font-semibold">{project.title}</h3>
        <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
          <span className="flex items-center gap-1">
            {project.platform === "mobile" ? (
              <Smartphone className="h-3.5 w-3.5" />
            ) : (
              <Monitor className="h-3.5 w-3.5" />
            )}
            {project.platform === "mobile"
              ? `Test ${project.appName} App`
              : `Test ${project.appName} Web App`}
          </span>
        </div>
      </CardHeader>
      <CardContent className="flex-1 pb-3">
        <p className="text-sm text-muted-foreground line-clamp-3">{project.instructions}</p>
        <div className="mt-4 flex items-center justify-between text-xs text-muted-foreground">
          <span className="flex items-center gap-1">
            <Calendar className="h-3.5 w-3.5" />
            Deadline: {formatDate(project.deadline)}
          </span>
          <span className="flex items-center gap-1">
            <User className="h-3.5 w-3.5" />
            {project.owner}
          </span>
        </div>
        {project.requirements && project.requirements.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {project.requirements.map((req, idx) => (
              <span
                key={idx}
                className="flex items-center gap-1 rounded-lg bg-slate-100 px-2 py-1 text-[10px] font-medium text-slate-600"
              >
                <CheckCircle2 className="h-3 w-3" />
                {req}
              </span>
            ))}
          </div>
        )}
        <div className="mt-3 rounded-xl bg-emerald-50 border border-emerald-100 px-3 py-2 text-center">
          <span className="text-[11px] font-semibold text-emerald-700 uppercase tracking-wide">
            Test Payout
          </span>
          <p className="text-lg font-bold text-emerald-700 tabular-nums mt-0.5">
            {formatMoney(project.reward)}
          </p>
          <p className="text-[11px] text-emerald-700 font-medium">Paid after completion</p>
        </div>
      </CardContent>
      <CardFooter className="pt-3">
        <Button
          className={`w-full ${isLocked ? "bg-amber-600 hover:bg-amber-700" : completed ? "bg-emerald-600 hover:bg-emerald-700" : "bg-blue-500 hover:bg-green-600"} text-white`}
          onClick={() => (isLocked && onLockedClick ? onLockedClick(project) : onPreview(project))}
          disabled={completed && !isLocked}
        >
          {isLocked ? (
            <>
              <Lock className="mr-2 h-4 w-4" />
               Unlock All With $2
            </>
          ) : completed ? (
            <>
              <CheckCircle2 className="mr-2 h-4 w-4" />
              Completed
            </>
          ) : (
            <>
              <Eye className="mr-2 h-4 w-4" />
              Start Test
            </>
          )}
        </Button>
      </CardFooter>
    </Card>
  );
}
